/* =====================================================================
   FRANGIPANI — Journal article behaviour
   ---------------------------------------------------------------------
   Everything here works on the article HTML as it already exists in
   Shopify, so no post has to be rewritten to gain any of it:

     1. Notes      a paragraph that opens with a bold label, or with
                   Note/Tip/Safety/Remember/Good to know, is pulled out of
                   the run of text into a ruled aside.
     2. FAQ        a run of question headings at the end of a post — or
                   anything after a heading that says "FAQ" — folds into
                   an accordion, described to Google as FAQPage data.
     3. Product    one product card is placed partway down the article
                   rather than only at the end.
     4. Links      the first mention of a scent we sell becomes a link.
     5. Outline    the "In this article" list and the sticky rail are both
                   built from the article's own <h2>s.
     6. Progress   a hairline at the top of the window.
     7. Share      native share sheet where available, copy-link always.
     8. Cart       adding from the article without leaving the page.

   None of it is required for the page to read; if the script fails the
   article still renders as prose.
   ===================================================================== */

(function () {
  const root = document.querySelector('[data-fp-article]');
  if (!root) return;

  const body = root.querySelector('.fp-art__body');

  /* ------------------------------------------------------------------
     1. Notes
     ------------------------------------------------------------------ */

  const NOTE_WORDS = /^(note|tip|safety|remember|good to know|a note[^.:—-]*)\s*[:—-]\s*/i;

  function buildNotes() {
    if (!body) return;

    Array.from(body.querySelectorAll('p')).forEach((para) => {
      // A bold-labelled bullet is a list item, not an aside; leave list
      // and quote content in place.
      if (para.closest('li, blockquote')) return;

      let label = '';

      // "<strong>A note on FCF bergamot</strong> — body text"
      const lead = para.firstElementChild;
      if (lead && /^(STRONG|B)$/.test(lead.tagName) && lead === para.firstChild) {
        const text = lead.textContent.trim();
        const rest = para.textContent.slice(lead.textContent.length).trim();
        if (text.length <= 60 && rest.length > 40) {
          label = text.replace(/[:—-]\s*$/, '');
          lead.remove();
          para.innerHTML = para.innerHTML.replace(/^\s*[:—-]\s*/, '');
        }
      }

      // "Note: body text"
      if (!label) {
        const match = NOTE_WORDS.exec(para.textContent);
        if (match && para.textContent.length > 60) {
          label = match[1];
          const first = para.firstChild;
          if (first && first.nodeType === Node.TEXT_NODE) {
            first.nodeValue = first.nodeValue.replace(NOTE_WORDS, '');
          }
        }
      }

      if (!label) return;

      const note = document.createElement('aside');
      note.className = 'fp-art__note';

      const heading = document.createElement('p');
      heading.className = 'fp-art__label';
      heading.textContent = label;
      note.appendChild(heading);

      para.replaceWith(note);
      note.appendChild(para);
    });
  }

  /* ------------------------------------------------------------------
     2. FAQ accordion
     ------------------------------------------------------------------ */

  function collectUntilNextHeading(start) {
    const nodes = [];
    let node = start.nextElementSibling;
    while (node && !/^H[123]$/.test(node.tagName)) {
      nodes.push(node);
      node = node.nextElementSibling;
    }
    return nodes;
  }

  function buildFaq() {
    if (!body) return;

    const headings = Array.from(body.querySelectorAll('h2, h3'));
    if (!headings.length) return;

    const marker = headings.find((h) =>
      /^(faq|faqs|frequently asked questions)\b/i.test(h.textContent.trim())
    );

    let questions;
    if (marker) {
      questions = headings.slice(headings.indexOf(marker) + 1);
    } else {
      const trailing = [];
      for (let i = headings.length - 1; i >= 0; i--) {
        if (headings[i].textContent.trim().endsWith('?')) {
          trailing.unshift(headings[i]);
        } else {
          break;
        }
      }
      questions = trailing.length >= 3 ? trailing : [];
    }

    questions = questions.filter((h) => h.textContent.trim().endsWith('?'));
    if (questions.length < 2) return;

    const wrap = document.createElement('div');
    wrap.className = 'fp-art__faq';
    questions[0].parentNode.insertBefore(wrap, questions[0]);

    const schema = [];

    questions.forEach((heading) => {
      const answer = collectUntilNextHeading(heading);

      const details = document.createElement('details');
      const summary = document.createElement('summary');
      summary.textContent = heading.textContent.trim();
      details.appendChild(summary);

      const answerWrap = document.createElement('div');
      answerWrap.className = 'fp-art__faq-body';
      answer.forEach((node) => answerWrap.appendChild(node));
      details.appendChild(answerWrap);

      wrap.appendChild(details);
      heading.remove();

      schema.push({
        '@type': 'Question',
        name: summary.textContent,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerWrap.textContent.trim().slice(0, 1200),
        },
      });
    });

    if (schema.length) {
      const tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: schema,
      });
      document.head.appendChild(tag);
    }
  }

  /* ------------------------------------------------------------------
     3. One product card, partway down
     ------------------------------------------------------------------ */

  function placeInlineProduct() {
    const template = root.querySelector('template[data-fp-inline-product]');
    if (!template || !body) return;

    const headings = Array.from(body.querySelectorAll('h2'));
    // Aim for a natural pause a third of the way in, never the very top.
    const target = headings[Math.min(2, headings.length - 1)];

    if (target && headings.indexOf(target) > 0) {
      target.parentNode.insertBefore(template.content.cloneNode(true), target);
    }
  }

  /* ------------------------------------------------------------------
     4. Link the first mention of each scent we sell
     ------------------------------------------------------------------ */

  function linkProducts() {
    if (!body) return;

    const data = root.querySelector('[data-fp-products]');
    if (!data) return;

    let products;
    try {
      products = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(products) || !products.length) return;

    // Longest names first, so "Litsea Cubeba" wins over "Litsea".
    products.sort((a, b) => b.name.length - a.name.length);

    const linked = new Set();
    let budget = 6;

    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (
          node.parentElement.closest(
            'a, h1, h2, h3, h4, summary, .fp-shop, .fp-art__inline-product, .fp-art__label'
          )
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const targets = [];
    let node;
    while ((node = walker.nextNode())) targets.push(node);

    for (const text of targets) {
      if (budget <= 0) break;

      for (const product of products) {
        if (budget <= 0) break;
        if (linked.has(product.url)) continue;

        const pattern = new RegExp(
          `\\b${product.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
          'i'
        );
        const match = pattern.exec(text.nodeValue);
        if (!match) continue;

        const after = text.splitText(match.index);
        after.nodeValue = after.nodeValue.slice(match[0].length);

        const link = document.createElement('a');
        link.className = 'fp-art__prod-link';
        link.href = product.url;
        link.textContent = match[0];
        after.parentNode.insertBefore(link, after);

        linked.add(product.url);
        budget -= 1;
        break;
      }
    }
  }

  /* ------------------------------------------------------------------
     5. Outline — the in-body list and the sticky rail
     ------------------------------------------------------------------ */

  function buildOutline() {
    if (!body) return [];

    const rail = root.querySelector('[data-fp-toc]');
    const short = root.querySelector('[data-fp-short-list]');
    const shortBlock = root.querySelector('[data-fp-short]');

    const headings = Array.from(body.querySelectorAll('h2')).filter(
      (h) => h.textContent.trim().length > 0
    );

    // Two headings is a list, not an outline.
    if (headings.length < 3) {
      const wrap = root.querySelector('.fp-art__toc-wrap');
      if (wrap) wrap.hidden = true;
      if (shortBlock && !shortBlock.hasAttribute('data-fp-authored')) shortBlock.hidden = true;
      return [];
    }

    const links = [];

    headings.forEach((heading, index) => {
      if (!heading.id) {
        const slug = heading.textContent
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .slice(0, 60);
        heading.id = slug ? `${slug}-${index}` : `section-${index}`;
      }

      const title = heading.textContent.trim();

      if (rail) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = title;
        item.appendChild(link);
        rail.appendChild(item);
        links.push({ link, heading });
      }

      // The in-body list is skipped when the shop has written its own
      // key points into the article metafield.
      if (short && !shortBlock.hasAttribute('data-fp-authored')) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = title;
        item.appendChild(link);
        short.appendChild(item);
      }
    });

    return links;
  }

  /* ------------------------------------------------------------------
     6. Reading progress and the current section
     ------------------------------------------------------------------ */

  function trackReading(links) {
    const bar = root.querySelector('[data-fp-progress]');
    if (!bar && !links.length) return;

    let ticking = false;

    const update = () => {
      ticking = false;

      if (bar && body) {
        const start = body.offsetTop;
        const distance = body.offsetHeight - window.innerHeight * 0.4;
        const scrolled = window.scrollY - start;
        const ratio = distance > 0 ? scrolled / distance : 0;
        bar.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
      }

      if (links.length) {
        const line = window.scrollY + window.innerHeight * 0.28;
        let current = links[0];
        for (const entry of links) {
          if (entry.heading.getBoundingClientRect().top + window.scrollY <= line) {
            current = entry;
          }
        }
        for (const entry of links) {
          if (entry === current) {
            entry.link.setAttribute('aria-current', 'true');
          } else {
            entry.link.removeAttribute('aria-current');
          }
        }
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------
     7. Share
     ------------------------------------------------------------------ */

  function wireShare() {
    const copy = root.querySelector('[data-fp-copy]');
    if (copy) {
      const label = copy.textContent;
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
        } catch (error) {
          return;
        }
        copy.classList.add('is-copied');
        copy.textContent = 'Copied';
        setTimeout(() => {
          copy.classList.remove('is-copied');
          copy.textContent = label;
        }, 1800);
      });
    }

    const native = root.querySelector('[data-fp-share-native]');
    if (native) {
      if (!navigator.share) {
        native.hidden = true;
      } else {
        native.hidden = false;
        native.addEventListener('click', () => {
          navigator
            .share({ title: document.title, url: window.location.href })
            .catch(() => {});
        });
      }
    }
  }

  /* ------------------------------------------------------------------
     8. Add to cart
     ------------------------------------------------------------------
     data-fp-add holds one variant id, or several separated by commas —
     the right rail's card adds every scent in the story at once. */

  function wireAddToCart() {
    root.querySelectorAll('[data-fp-add]').forEach((button) => {
      if (button.dataset.fpWired) return;
      button.dataset.fpWired = 'true';

      button.addEventListener('click', async () => {
        const ids = (button.getAttribute('data-fp-add') || '')
          .split(',')
          .map((value) => Number(value))
          .filter(Boolean);
        if (!ids.length || button.disabled) return;

        const label = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
          });
          if (!response.ok) throw new Error('add failed');
        } catch (error) {
          button.disabled = false;
          button.textContent = label;
          return;
        }

        button.classList.add('is-added');
        button.textContent = 'Added';
        button.disabled = false;

        const link = button.parentElement.querySelector('.fp-shop__cart-link');
        if (link) link.hidden = false;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
      });
    });
  }

  /* ------------------------------------------------------------------ */

  buildNotes();
  buildFaq();
  placeInlineProduct();
  linkProducts();
  trackReading(buildOutline());
  wireShare();
  wireAddToCart();
})();
